import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Select, Spin, Tabs, Tag, message } from 'antd';
import { LeftOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { getStoredUser, isRegionManager, isSuperAdmin } from '../userAuth';

const statusMap = {
  0: { text: '待盘点', color: 'gold' },
  1: { text: '盘点中', color: 'blue' },
  2: { text: '已完成', color: 'green' },
};

export default function Inventory() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const canUse = isSuperAdmin(user) || isRegionManager(user);
  const [tab, setTab] = useState('tasks');
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const loadTasks = useCallback(() => {
    setLoading(true);
    api.get('/inventory/tasks', { params: { page: 1, page_size: 50 } })
      .then((res) => setTasks(res?.items || []))
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const loadLogs = useCallback(() => {
    setLoading(true);
    api.get('/inventory/logs', { params: { page: 1, page_size: 50 } })
      .then((res) => setLogs(res?.items || []))
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canUse) return;
    if (tab === 'tasks') loadTasks();
    else loadLogs();
  }, [tab, canUse, loadTasks, loadLogs]);

  const openCreate = async () => {
    form.resetFields();
    form.setFieldsValue({
      title: `${dayjs().format('YYYY年M月')}盘点`,
      assignee_id: isSuperAdmin(user) ? undefined : user?.id,
    });
    if (isSuperAdmin(user)) {
      try {
        const res = await api.get('/inventory/managers');
        setManagers(res?.items || []);
      } catch {
        setManagers([]);
      }
    }
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        title: values.title.trim(),
        assignee_id: isSuperAdmin(user) ? values.assignee_id : user.id,
        location_ids: [],
      };
      const res = await api.post('/inventory/tasks', payload);
      message.success('盘点任务已创建');
      setCreateOpen(false);
      loadTasks();
      if (res?.id) navigate(`/inventory/${res.id}`);
    } catch (err) {
      if (err?.message) message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canUse) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: 24, textAlign: 'center' }}>
        <div style={{ marginTop: 80, color: '#999' }}>仅区域管理员与超级管理员可使用资产盘点</div>
        <Button style={{ marginTop: 16 }} onClick={() => navigate(-1)}>返回</Button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 70 }}>
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 16px',
        background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>资产盘点</span>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          {isSuperAdmin(user) ? '下达任务' : '开始盘点'}
        </Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        centered
        items={[
          { key: 'tasks', label: '盘点任务' },
          { key: 'logs', label: '盘点日志' },
        ]}
        style={{ background: '#fff', marginBottom: 0 }}
      />

      <div style={{ padding: 16 }}>
        <Spin spinning={loading}>
          {tab === 'tasks' && (
            tasks.length === 0 && !loading ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>暂无盘点任务</div>
            ) : (
              tasks.map((t) => {
                const st = statusMap[t.status] || statusMap[0];
                return (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/inventory/${t.id}`)}
                    style={{
                      background: '#fff', borderRadius: 10, padding: 14, marginBottom: 10,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#222' }}>{t.title}</span>
                      <Tag color={st.color}>{st.text}</Tag>
                    </div>
                    <div style={{ fontSize: 13, color: '#666', lineHeight: 1.7 }}>
                      <div>执行人：{t.assignee_name || '-'}</div>
                      <div>进度：已盘 {t.checked_count || 0} / 共 {t.total_count || 0}（未盘 {t.unchecked_count || 0}）</div>
                      <div style={{ color: '#999', fontSize: 12 }}>
                        {t.create_time ? dayjs(t.create_time).format('YYYY-MM-DD HH:mm') : ''}
                        {t.created_by_name ? ` · ${t.created_by_name} 创建` : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}

          {tab === 'logs' && (
            logs.length === 0 && !loading ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>暂无盘点日志</div>
            ) : (
              logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    background: '#fff', borderRadius: 10, padding: 14, marginBottom: 10,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                    {log.create_time ? dayjs(log.create_time).format('M月D日') : ''} · {log.user_name}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', lineHeight: 1.7 }}>
                    <div>任务：{log.title}</div>
                    <div>区域：{log.location_summary || '-'}</div>
                    <div>数量：已盘 {log.checked_count}/{log.total_count} 件</div>
                    <div>
                      情况：{Number(log.feedback_ok) === 1 ? '全部符合' : '存在问题'}
                      {log.feedback ? ` — ${log.feedback}` : ''}
                    </div>
                  </div>
                </div>
              ))
            )
          )}
        </Spin>
      </div>

      <Modal
        title={isSuperAdmin(user) ? '下达盘点任务' : '开始本区域盘点'}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={submitting}
        okText="创建"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="title" label="任务标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="如：2026年3月盘点" maxLength={100} />
          </Form.Item>
          {isSuperAdmin(user) && (
            <Form.Item name="assignee_id" label="执行人" rules={[{ required: true, message: '请选择' }]}>
              <Select
                placeholder="选择执行人"
                showSearch
                optionFilterProp="label"
                options={[
                  {
                    value: user.id,
                    label: `${user.username || '超管'}（自己盘点全部资产）`,
                  },
                  ...managers.map((m) => ({
                    value: m.id,
                    label: `${m.username}${m.work_no ? `（${m.work_no}）` : ''} · ${m.location_count || 0}个地点`,
                  })),
                ]}
              />
            </Form.Item>
          )}
          <div style={{ fontSize: 12, color: '#999' }}>
            {isSuperAdmin(user)
              ? '不指定地点时，执行人将盘点其管辖区域内全部资产。'
              : '将按你管辖区域内的全部资产进行盘点。'}
          </div>
        </Form>
      </Modal>
    </div>
  );
}
